import { IconButton, Menu, MenuItem, SvgIcon, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { borderRadius, boxShadows, palette, spaces } from '../../assets/styles/themes/tokens';
import getMuiIcon from '../../utils/getMuiIcon';

interface PublicPageActionsMenuProps {
	onPrint: () => Promise<void>;
	onDownload: () => Promise<void>;
	isPrintDisabled?: boolean;
	isDownloadDisabled?: boolean;
}

/**
 * Three-dot menu for public page actions (Print/Download) on mobile.
 * Similar to AIAssistantTextEditorExportButton but without share/context dependencies.
 */
const PublicPageActionsMenu: React.FC<PublicPageActionsMenuProps> = ({
	onPrint,
	onDownload,
	isPrintDisabled = false,
	isDownloadDisabled = false,
}) => {
	const { t } = useTranslation();
	const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

	const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setMenuAnchor(event.currentTarget);
	};

	const handleMenuClose = () => {
		setMenuAnchor(null);
	};

	const handleDownloadClick = async () => {
		handleMenuClose();
		await onDownload();
	};

	const handlePrintClick = async () => {
		handleMenuClose();
		await onPrint();
	};

	return (
		<>
			<IconButton
				data-testid="public-page-actions-menu-button"
				onClick={handleMenuClick}
				sx={{
					padding: spaces.xs,
				}}
			>
				<SvgIcon
					component={getMuiIcon('moreCircle')}
					sx={{
						width: 35,
						height: 35,
						fill: palette.text.primary,
					}}
					viewBox="0 0 35 35"
				/>
			</IconButton>
			<Menu
				anchorEl={menuAnchor}
				open={Boolean(menuAnchor)}
				onClose={handleMenuClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				sx={{
					zIndex: 1500,
					'& .MuiMenu-paper': {
						borderRadius: borderRadius.md,
						boxShadow: boxShadows.elevation4,
						padding: `0 ${spaces.xs}`,
					},
				}}
			>
				<MenuItem
					data-testid="menu-item-print"
					onClick={handlePrintClick}
					disabled={isPrintDisabled}
					sx={{
						borderRadius: borderRadius.sm,
					}}
				>
					<SvgIcon
						component={getMuiIcon('printer')}
						sx={{ fill: palette.text.primary, marginRight: spaces.md }}
					/>
					<Typography>{t('Print')}</Typography>
				</MenuItem>
				<MenuItem
					data-testid="menu-item-download"
					onClick={handleDownloadClick}
					disabled={isDownloadDisabled}
					sx={{
						borderRadius: borderRadius.sm,
					}}
				>
					<SvgIcon
						component={getMuiIcon('download')}
						sx={{
							fill: palette.text.primary,
							color: palette.text.primary,
							marginRight: spaces.md,
						}}
					/>
					<Typography>{t('Download')}</Typography>
				</MenuItem>
			</Menu>
		</>
	);
};

export default PublicPageActionsMenu;
